<script setup lang="ts">
import BaseSpinner from "@/components/Base/Spinner.vue";
import LazyImage from "@/components/LazyImage.vue";
import { AiConversation } from "@/services/fobizzToolsApi";

type Props = {
  assistant: AiConversation["assistant"];
  isGenerating?: boolean;
  size?: "xxs" | "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
  editable?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  size: "md",
  isGenerating: false,
  editable: false,
});

const imageUrl = computed(() => {
  if (props.assistant?.image) {
    if (props.size === "xl") {
      return props.assistant.image.url;
    } else if (props.assistant?.image.smallPreviewUrl) {
      return props.assistant.image.smallPreviewUrl;
    } else {
      return props.assistant.image.url;
    }
  }

  return "";
});
</script>

<template>
  <div
    class="assistant-avatar"
    :class="[
      `assistant-avatar--${size}`,
      { 'assistant-avatar--editable': editable && !imageUrl },
    ]"
  >
    <BaseSpinner v-if="isGenerating" />
    <div v-else>
      <LazyImage v-if="imageUrl" :src="imageUrl" alt="avatar" />
      <div v-else class="assistant-avatar__placeholder">
        <i class="bi bi-stars" style="margin-top: -2px"></i>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.assistant-avatar {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: $gray-100;
  overflow: hidden;
  flex: none;

  img {
    max-width: 100%;
    height: auto;
  }

  &--editable &__placeholder {
    color: $gray-400;
  }

  &--xxs {
    width: 24px;
    height: 24px;
  }

  &--xxs &__placeholder {
    font-size: 1rem;
  }

  &--xs {
    width: 32px;
    height: 32px;
  }

  &--xs &__placeholder {
    font-size: 1rem;
  }

  &--sm {
    width: 40px;
    height: 40px;
  }

  &--sm &__placeholder {
    font-size: 1.2rem;
  }

  &--md {
    width: 60px;
    height: 60px;
  }

  &--md &__placeholder {
    font-size: 1.4rem;
  }

  &--lg {
    width: 96px;
    height: 96px;

    @include media-breakpoint-down(md) {
      width: 60px;
      height: 60px;
    }
  }

  &--lg &__placeholder {
    font-size: 3rem;

    @include media-breakpoint-down(md) {
      font-size: 2rem;
    }
  }

  &--xl {
    width: 120px;
    height: 120px;

    @include media-breakpoint-down(md) {
      width: 90px;
      height: 90px;
    }
  }

  &--xl &__placeholder {
    font-size: 3rem;

    @include media-breakpoint-down(md) {
      font-size: 2rem;
    }
  }

  &--xxl {
    width: 160px;
    height: 160px;
  }

  &--xxl &__placeholder {
    font-size: 6rem;
    padding-bottom: 1px;
  }

  &--editable {
    border: 3px dashed $border-color;
    background-color: $color-surface-white;
  }
}
</style>
